import type { MutableRefObject, RefObject } from 'react';
import { CONTENT_HEIGHT_ATTRIBUTE, setSheetStateAttribute } from '../sheetStateAttributes';
import type { SheetEngineProps } from '../sheetTypes';
import { getSheetSnapPointWindowHeight, getSnapPointSheetHeight } from './sheetHeightUtils';
import { MAX_SUFFIX, getDefaultSnapPointDetails } from './snapPointUtils';

const getInitialSnapPoint = (
    defaultSnapPoint: number | string | undefined,
    sheetRef: RefObject<HTMLDivElement>,
    topInset: number,
): number | undefined => {
    const { point, suffix } = getDefaultSnapPointDetails(defaultSnapPoint);
    const defaultSnapIsMax = suffix && point && suffix === MAX_SUFFIX;

    if (point && !defaultSnapIsMax) {
        return point;
    }

    if (!sheetRef.current) return;

    const contentHeightAsDecimal = sheetRef.current.clientHeight / getSheetSnapPointWindowHeight(topInset);
    const maxInitialSnapPoint = defaultSnapIsMax ? point : 1;
    return Math.min(maxInitialSnapPoint, contentHeightAsDecimal);
};

const setInitialSheetHeight = ({
    sheetProps,
    addSnapPoint,
    setSheetInitialised,
    cancelInitialHeightAnimationFrameRef,
    cancelInProgressAnimations,
}: {
    sheetProps: SheetEngineProps;
    addSnapPoint: (snapPoint: number) => void;
    setSheetInitialised: (sheetInitialised: boolean) => void;
    cancelInitialHeightAnimationFrameRef: MutableRefObject<(() => void) | null>;
    cancelInProgressAnimations: () => void;
}) => {
    const {
        sheetRef,
        sheetContentRef,
        defaultSnapPoint,
        preventContentHeightChange = false,
        onOpenTransitionEnd = () => {},
        onSnapPointChange,
        topInset = 0,
    } = sheetProps;

    if (!sheetRef.current || !sheetContentRef.current) return;

    sheetContentRef.current.style.height = 'auto';
    setSheetStateAttribute(sheetRef.current, CONTENT_HEIGHT_ATTRIBUTE, true);

    const frameId = requestAnimationFrame(() => {
        if (!sheetRef.current || !sheetContentRef.current) return;

        cancelInProgressAnimations();

        const initialSnapPoint = getInitialSnapPoint(defaultSnapPoint, sheetRef, topInset);
        if (!initialSnapPoint) return;

        setSheetStateAttribute(sheetRef.current, CONTENT_HEIGHT_ATTRIBUTE, false);
        addSnapPoint(initialSnapPoint);
        onSnapPointChange(initialSnapPoint);
        setSheetInitialised(true);

        // If the sheet is fixed height, we need to set the initial content height right after initialising
        if (preventContentHeightChange) {
            const sheetRect = sheetRef.current.getBoundingClientRect();
            const sheetContentRect = sheetContentRef.current.getBoundingClientRect();

            const initialSnapPointPx = getSnapPointSheetHeight(initialSnapPoint, topInset);
            const initialContentHeight = initialSnapPointPx + sheetRect.top - sheetContentRect.top;

            sheetContentRef.current.style.height = `${initialContentHeight}px`;
        }

        sheetRef.current.addEventListener('transitionend', onOpenTransitionEnd, { once: true });
    });

    cancelInitialHeightAnimationFrameRef.current = () => {
        cancelAnimationFrame(frameId);
        cancelInitialHeightAnimationFrameRef.current = null;
    };
};

export default setInitialSheetHeight;
